import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { ICustomizableTableColumn } from "constants/table-types";
import { defaultTableRowRenderer } from "react-virtualized/dist/commonjs/index";
import { AutoSizer, Column, Table, TableCellProps, TableRowProps } from "react-virtualized";
import { SortableContainer, SortableElement } from "react-sortable-hoc";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { styles } from "components/table/styles";
import { format } from "lib/globalize-util";
import { useDebounceFunction as useDebounce } from "hooks";
import TextField from "components/form/text-field";
import ToolbarGroup from "components/page/action-bar";
import SearchIcon from "@material-ui/icons/Search";
import { themeOptions } from "styles/theme";
import { Theme } from "@material-ui/core/styles";

const TABLE_HEADER_HEIGHT = 48;
const TABLE_ROW_HEIGHT = 60;

interface IProps<Row extends object> {
    columnsConfig: ICustomizableTableColumn[];
    rows: Array<Row>;
    onIsRowMatchesSearch: (row: Row, search: string) => boolean;
    onUpdateRowIndex?: (oldIndex: number, newIndex: number) => void;
    cellRenderer?: (cellProps: TableCellProps & { rowData: Row }) => ReactNode;
    useSearch?: boolean;
    noRowsFoundDataTest?: string;
    searchDataTest?: string;
    tableHeight?: number;
}

const SortableTableRowRenderer = SortableElement((props: { rowProps: TableRowProps }) =>
    defaultTableRowRenderer(props.rowProps),
);
const SortableTable = SortableContainer(Table);

export const SortableTableWithSearch = <Row extends object>(props: IProps<Row>) => {
    const {
        columnsConfig,
        rows,
        cellRenderer,
        onUpdateRowIndex,
        onIsRowMatchesSearch,
        useSearch = true,
        ...rest
    } = props;
    const { noRowsFoundDataTest, searchDataTest, tableHeight = 400 } = props;

    const classes = useStyles({ tableHeight });
    const styles = useInlineStyles();

    const [rowsToShow, setRowsToShow] = useState<Row[]>([]);

    const [searchText, setSearchText] = useState("");

    const searchTextCallback = useCallback((event, value) => setSearchText(value), []);
    const setSearchTextDebounced = useDebounce(searchTextCallback, 500);

    useEffect(() => {
        setRowsToShow(_.isEmpty(searchText) ? rows : _.filter(rows, (row) => onIsRowMatchesSearch(row, searchText)));
    }, [searchText, rows, onIsRowMatchesSearch]);

    const fullWidth = useMemo<number>(() => _.sumBy(columnsConfig, (column) => column.width + 10), [columnsConfig]);

    const rowGetter = useCallback(({ index }) => rowsToShow[index], [rowsToShow]);

    const noRowRenderer = useCallback(
        () => (
            <div data-test={noRowsFoundDataTest} className={classes.noResults}>
                {format("No results found")}
            </div>
        ),
        [noRowsFoundDataTest, classes],
    );

    const rowRenderer = useCallback((rowProps: TableRowProps) => {
        if (rowProps.rowData) {
            return <SortableTableRowRenderer key={rowProps.key} index={rowProps.index} rowProps={rowProps} />;
        }
    }, []);

    const onSortEnd = useCallback(
        ({ oldIndex, newIndex }) => {
            onUpdateRowIndex(oldIndex, newIndex);
        },
        [onUpdateRowIndex],
    );

    return (
        <div>
            {useSearch && (
                <ToolbarGroup>
                    <div key="search" className={classes.searchFieldWrapper}>
                        <SearchIcon className={classes.searchIcon} />
                        <TextField
                            dataTest={searchDataTest}
                            name="doors-search"
                            defaultValue={searchText}
                            onChange={setSearchTextDebounced}
                            placeholder={format("Search")}
                            canClear={true}
                            large={false}
                            fullWidth={true}
                            inputStyle={styles.smallInput}
                            allowWhitespace={true}
                        />
                    </div>
                </ToolbarGroup>
            )}

            <div className={classes.autoSizerContainer}>
                <AutoSizer className={classes.contentRoot}>
                    {({ width, height }) => (
                        <SortableTable
                            className={classes.table}
                            // sort props
                            helperClass={classes.helper}
                            onSortEnd={onSortEnd}
                            // table props
                            useDragHandle={true}
                            height={height}
                            width={Math.max(width, fullWidth)}
                            headerHeight={TABLE_HEADER_HEIGHT}
                            rowHeight={TABLE_ROW_HEIGHT}
                            rowCount={rowsToShow.length}
                            rowGetter={rowGetter}
                            rowRenderer={rowRenderer}
                            noRowsRenderer={noRowRenderer}
                            {...rest}
                        >
                            {_.map(columnsConfig, (column: ICustomizableTableColumn) => (
                                <Column
                                    {...column}
                                    key={column.dataKey}
                                    minWidth={column.width}
                                    cellRenderer={cellRenderer}
                                />
                            ))}
                        </SortableTable>
                    )}
                </AutoSizer>
            </div>
        </div>
    );
};

const useStyles = makeStyles<Theme, { tableHeight: number }>({
    table: {
        ...styles,
        "& .ReactVirtualized__Grid__innerScrollContainer, .ReactVirtualized__Grid": undefined, // to show scroll inside of table
    },
    contentRoot: ({ tableHeight = 400 }) => ({
        minHeight: tableHeight,
    }),
    helper: {
        zIndex: "1500 !important" as any,
    },
    noResults: {
        textAlign: "center",
        maxWidth: 450,
        fontSize: 20,
        padding: "20px 10px",
        margin: "auto",
    },
    searchFieldWrapper: {
        position: "relative",
        width: "100%",
    },
    searchIcon: {
        position: "absolute",
        top: "50%",
        left: 8,
        transform: "translateY(-50%)",
        zIndex: 2,
        fill: themeOptions.palette.grey,
        height: 20,
        width: 20,
    },
    autoSizerContainer: ({ tableHeight = 400 }) => ({
        height: tableHeight,
        display: "flex",
    }),
});

const useInlineStyles = () =>
    useMemo(
        () => ({
            smallInput: {
                width: "100%",
                height: "50px",
                fontSize: "16px",
                paddingLeft: "36px",
                border: `1px solid ${themeOptions.palette.lightGrey}`,
            },
        }),
        [],
    );
